import React, { useState } from 'react';
import {
  Box, Button, IconButton, InputBase, Menu, MenuItem, Paper, Snackbar, Stack, Typography,
} from '@mui/material';
import {
  AddRounded, CloseRounded, DescriptionOutlined, DragIndicatorRounded,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';

export const MAX_CUSTOM_PREPARATION_ITEMS = 12;

const itemTypeLabel = {
  prepared_information: 'Lifelog will prepare this',
  records_plus_caregiver: 'Lifelog will help draft this',
  caregiver_only: 'You’ll answer this',
};

const reorder = (items, from, to) => {
  if (to < 0 || to >= items.length || from === to) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

const alignBriefStructure = (proposal, preparationItems) => {
  const sectionByQuestionId = new Map(proposal.briefStructure.map((section) => [section.questionId, section]));
  return {
    ...proposal,
    preparationItems,
    briefStructure: preparationItems.map((item) => sectionByQuestionId.get(item.id)).filter(Boolean),
  };
};

const CustomJourneyPlanEditor = ({ proposal, onChange, makeQuestionId }) => {
  const [removed, setRemoved] = useState(null);
  const [menu, setMenu] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const items = proposal.preparationItems;
  const documentSuggestions = proposal.documentSuggestions || [];

  const updateLabel = (index, label) => {
    const preparationItems = items.map((item, itemIndex) => itemIndex === index ? { ...item, label } : item);
    const next = alignBriefStructure(proposal, preparationItems);
    next.briefStructure = next.briefStructure.map((section) => (
      section.questionId === items[index].id ? { ...section, label: label.trim() || section.label } : section
    ));
    onChange(next);
  };
  const move = (index, direction) => {
    onChange(alignBriefStructure(proposal, reorder(items, index, index + direction)));
    setMenu(null);
  };
  const remove = (index) => {
    if (items.length <= 1) return;
    const item = items[index];
    const section = proposal.briefStructure.find((candidate) => candidate.questionId === item.id);
    setRemoved({ item, section, index });
    onChange({
      ...proposal,
      preparationItems: items.filter((_, itemIndex) => itemIndex !== index),
      briefStructure: proposal.briefStructure.filter((candidate) => candidate.questionId !== item.id),
    });
  };
  const undoRemove = () => {
    if (!removed) return;
    const preparationItems = [...items];
    preparationItems.splice(Math.min(removed.index, preparationItems.length), 0, removed.item);
    const sections = [...proposal.briefStructure];
    if (removed.section) sections.splice(Math.min(removed.index, sections.length), 0, removed.section);
    onChange({ ...proposal, preparationItems, briefStructure: sections });
    setRemoved(null);
  };
  const add = () => {
    if (items.length >= MAX_CUSTOM_PREPARATION_ITEMS) return;
    const id = makeQuestionId();
    onChange({
      ...proposal,
      preparationItems: [...items, { id, label: '', itemType: 'caregiver_only', evidenceIntentRefs: [] }],
      briefStructure: [...proposal.briefStructure, { id: `${id}_section`, label: 'Additional information', questionId: id }],
    });
  };
  const dropAt = (targetIndex) => {
    if (draggedIndex === null) return;
    onChange(alignBriefStructure(proposal, reorder(items, draggedIndex, targetIndex)));
    setDraggedIndex(null);
  };
  const removeDocumentSuggestion = (suggestionId) => {
    onChange({
      ...proposal,
      documentSuggestions: documentSuggestions.filter((suggestion) => suggestion.id !== suggestionId),
    });
  };

  return (
    <Box>
      <Stack spacing={1.25}>
        {items.map((item, index) => (
          <Paper
            key={item.id}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => dropAt(index)}
            elevation={0}
            sx={{
              display: 'grid', gridTemplateColumns: { xs: '42px minmax(0, 1fr) auto', sm: '58px minmax(0, 1fr) auto' },
              alignItems: 'center', minHeight: { xs: 96, sm: 110 }, overflow: 'hidden',
              borderRadius: '20px', borderLeft: `4px solid ${item.itemType === 'prepared_information' ? '#69AFA3' : '#7A61D1'}`,
              bgcolor: '#FFFFFF', boxShadow: `0 5px 20px ${alpha(colors.semantic.ink, 0.045)}`,
            }}
          >
            <Typography sx={{ pl: { xs: 1.1, sm: 1.7 }, fontSize: { xs: '1.25rem', sm: '1.45rem' }, fontWeight: 800, color: '#765CCB' }}>
              {index + 1}.
            </Typography>
            <Box sx={{ py: 1.4, px: { xs: 0.5, sm: 1 } }}>
              <InputBase
                fullWidth multiline
                value={item.label}
                onChange={(event) => updateLabel(index, event.target.value)}
                inputProps={{ 'aria-label': `Preparation item ${index + 1}`, maxLength: 280 }}
                sx={{ fontSize: { xs: '0.96rem', sm: '1.05rem' }, lineHeight: 1.45, color: colors.semantic.ink }}
              />
              <Typography sx={{ mt: 0.35, fontSize: '0.72rem', color: alpha(colors.semantic.ink, 0.52) }}>
                {itemTypeLabel[item.itemType] || itemTypeLabel.records_plus_caregiver}
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" sx={{ pr: { xs: 0.5, sm: 1 } }}>
              <IconButton
                aria-label={`Reorder preparation item ${index + 1}`}
                onClick={(event) => setMenu({ anchorEl: event.currentTarget, index })}
                draggable
                onDragStart={() => setDraggedIndex(index)}
                onDragEnd={() => setDraggedIndex(null)}
                sx={{ cursor: 'grab', color: alpha(colors.semantic.ink, 0.42) }}
              >
                <DragIndicatorRounded />
              </IconButton>
              <IconButton
                aria-label={`Remove preparation item ${index + 1}`}
                disabled={items.length <= 1}
                onClick={() => remove(index)}
                sx={{ color: alpha(colors.semantic.ink, 0.48) }}
              >
                <CloseRounded />
              </IconButton>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Menu anchorEl={menu?.anchorEl} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem disabled={menu?.index === 0} onClick={() => move(menu.index, -1)}>Move up</MenuItem>
        <MenuItem disabled={menu?.index === items.length - 1} onClick={() => move(menu.index, 1)}>Move down</MenuItem>
      </Menu>

      <Button startIcon={<AddRounded />} disabled={items.length >= MAX_CUSTOM_PREPARATION_ITEMS} onClick={add} sx={{ mt: 1.2 }}>
        Add a question
      </Button>
      {items.length >= MAX_CUSTOM_PREPARATION_ITEMS ? (
        <Typography sx={{ mt: 0.35, fontSize: '0.76rem', color: alpha(colors.semantic.ink, 0.58) }}>
          A preparation plan can include up to {MAX_CUSTOM_PREPARATION_ITEMS} items.
        </Typography>
      ) : null}

      {documentSuggestions.length ? (
        <Paper elevation={0} sx={{ mt: 2, p: { xs: 1.5, sm: 2 }, borderRadius: '20px' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DescriptionOutlined sx={{ color: '#765CCB' }} />
            <Box>
              <Typography sx={{ fontWeight: 800 }}>Documents that may help</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: alpha(colors.semantic.ink, 0.6) }}>
                Optional suggestions for this preparation. Remove anything you do not need.
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={0.8} sx={{ mt: 1.2 }}>
            {documentSuggestions.map((suggestion) => (
              <Box key={suggestion.id} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 1, p: 1.1, borderRadius: '14px', bgcolor: alpha('#765CCB', 0.045) }}>
                <Box>
                  <Typography sx={{ fontWeight: 750 }}>{suggestion.label}</Typography>
                  <Typography sx={{ mt: 0.2, fontSize: '0.8rem', color: alpha(colors.semantic.ink, 0.62) }}>{suggestion.reason}</Typography>
                </Box>
                <IconButton aria-label={`Remove ${suggestion.label}`} onClick={() => removeDocumentSuggestion(suggestion.id)}>
                  <CloseRounded />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </Paper>
      ) : null}

      <Snackbar
        open={Boolean(removed)}
        autoHideDuration={6000}
        onClose={() => setRemoved(null)}
        message="Preparation item removed"
        action={<Button color="inherit" onClick={undoRemove}>Undo</Button>}
      />
    </Box>
  );
};

export default CustomJourneyPlanEditor;
